import React, { useEffect, useMemo, useState } from 'react';
import styles from './ReorderListModal.module.css';

function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

function moveItem(items, fromIndex, toIndex) {
  if (fromIndex === toIndex) return items;
  const next = [...items];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

function normalizeId(id) {
  return String(id);
}

export default function ReorderListModal({
  isOpen,
  title,
  description,
  items,
  getId,
  getLabel,
  getColor,
  onClose,
  onSave,
  saving = false,
}) {
  const [localItems, setLocalItems] = useState([]);
  const [draggedId, setDraggedId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setLocalItems(items || []);
      setDraggedId(null);
    }
  }, [isOpen, items]);

  useEffect(() => {
    const clearDrag = () => setDraggedId(null);
    window.addEventListener('pointerup', clearDrag);
    window.addEventListener('pointercancel', clearDrag);
    return () => {
      window.removeEventListener('pointerup', clearDrag);
      window.removeEventListener('pointercancel', clearDrag);
    };
  }, []);

  const initialOrder = useMemo(() => (items || []).map((item) => getId(item)), [items, getId]);
  const currentOrder = useMemo(() => localItems.map((item) => getId(item)), [localItems, getId]);
  const hasChanges = !arraysEqual(initialOrder, currentOrder);

  const reorderByIds = (sourceId, targetId) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    setLocalItems((prev) => {
      const fromIndex = prev.findIndex((item) => normalizeId(getId(item)) === normalizeId(sourceId));
      const toIndex = prev.findIndex((item) => normalizeId(getId(item)) === normalizeId(targetId));
      if (fromIndex < 0 || toIndex < 0) return prev;
      return moveItem(prev, fromIndex, toIndex);
    });
  };

  const resetOrder = () => {
    setLocalItems(items || []);
    setDraggedId(null);
  };

  const handleSave = async () => {
    if (!hasChanges || saving) return;
    await onSave(localItems.map((item) => getId(item)));
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div className={styles.header}>
          <div>
            <h3 className={styles.title}>{title}</h3>
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close">
            x
          </button>
        </div>

        <div className={styles.list}>
          {localItems.map((item) => {
            const id = normalizeId(getId(item));
            const isDragged = draggedId === id;
            const color = getColor ? getColor(item) : null;

            return (
              <div
                key={id}
                className={`${styles.row} ${isDragged ? styles.rowDragging : ''}`}
                onPointerDown={(event) => {
                  if (event.button !== 0) return;
                  setDraggedId(id);
                }}
                onPointerEnter={() => {
                  if (draggedId && draggedId !== id) {
                    reorderByIds(draggedId, id);
                  }
                }}
                onPointerUp={() => setDraggedId(null)}
              >
                <span className={styles.grip} aria-hidden="true">::</span>
                {color ? <span className={styles.colorDot} style={{ backgroundColor: color }} /> : null}
                <span className={styles.label}>{getLabel(item)}</span>
              </div>
            );
          })}
        </div>

        <div className={styles.actions}>
          <button type="button" className={styles.secondary} onClick={resetOrder} disabled={saving || !hasChanges}>
            Reset
          </button>
          <button type="button" className={styles.secondary} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className={styles.primary} onClick={handleSave} disabled={saving || !hasChanges}>
            {saving ? 'Saving...' : 'Save Order'}
          </button>
        </div>
      </div>
    </div>
  );
}
